import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react'
import {
  Button,
  Container,
  Row,
  Col,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Modal,
} from 'reactstrap'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import WhiteFooter from 'components/Footers/WhiteFooter.js'
import logoMural from 'assets/img/logo_mural_2.png'
import Loading from 'components/Loading/Loading'
import ErrorText from 'components/Texts/ErrorText'
import { StudentService } from 'services/student'
import { StudentsHelper } from 'helpers/students'
import StudentsTable from 'components/Table/StudentsTable'
import { ValidationHelper } from 'helpers/validation'
import useUnsavedChangesWarning from 'hooks/useUnsavedChangesWarning'
import PanelContext from 'contexts/Panel'
import AuthContext from 'contexts/Auth'
import $RDStation from 'services/rdstation'

const schema = yup.object().shape({
  NomeCompleto: yup
    .string()
    .required('Por favor, informe o nome completo do estudante')
    .matches(ValidationHelper.onlyLetters, 'Apenas letras são permitidas')
    .nullable(),
})

const PesquisaIndividualPage = ({ history }) => {
  const { user } = useContext(AuthContext)
  const { year, semester, setYear, setSemester, years } = useContext(PanelContext)

  const [largeModal, setLargeModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isModalLoading, setIsModalLoading] = useState(false)
  const [studentsList, setStudentsList] = useState([])

  const { errors, watch, register, handleSubmit, setValue } = useForm({ resolver: yupResolver(schema) })

  const NomeCompletoWatch = watch('NomeCompleto')
  const { Prompt, setDirty } = useUnsavedChangesWarning(
    'Tem certeza que deseja sair? Seus dados podem não ter sido salvos.'
  )

  const goBack = useCallback(() => {
    history.push('/mural-aprovados-sisu')
  }, [history])

  const onSubmit = useCallback(
    async (value) => {
      setIsLoading(true)

      try {
        await $RDStation.conversation('consulta_mural_sisu', {
          company_name: user.NomeEscola,
          name: user.Nome,
          email: user.Email,
          city: user.Cidade,
          state: user.Estado,
          mobile_phone: user.Celular,
        })
      } finally {}

      try {
        const response = await StudentService.individually(value.NomeCompleto, year, semester)

        if (response.status === 200) {
          const data = StudentsHelper.formatList(response.data)
          setStudentsList(data)
          setValue('NomeCompleto', '')
        }
      } catch (e) {
        toast.error('Houve algum erro inesperado')
      } finally {
        setIsLoading(false)
      }
    },
    [user, year, semester, setValue]
  )

  const sendStudentList = useCallback(async () => {
    try {
      setIsModalLoading(true)
      const data = studentsList.map((student) => {
        if (student.Id <= 0) {
          return {
            IdAprovado: null,
            NomeDoAlunoNaoEncontrado: student.NomeInscrito,
          }
        }

        return {
          IdAprovado: student.Id,
          NomeDoAlunoNaoEncontrado: null,
        }
      })
      const response = await StudentService.sendStudents(data)
      if (response.status === 200) {
        toast.success('Lista de aprovados enviada com sucesso!')
        setStudentsList([])
        return true
      }
    } catch (e) {
      toast.error('Houve um erro inesperado')
    } finally {
      setIsModalLoading(false)
    }
  }, [studentsList])

  const handleExit = useCallback(async () => {
    const saved = await sendStudentList()
    if (saved) history.push('/mural-aprovados-sisu')
  }, [history, sendStudentList])

  useEffect(() => {
    if (NomeCompletoWatch?.length || studentsList?.length > 0) {
      setDirty(true)
    } else {
      setDirty(false)
    }
  }, [NomeCompletoWatch, setDirty, studentsList.length])

  const handleYearChange = ({ target: { value } }) => {
    const [ year, semester ] = value.split('.')
    setYear(parseInt(year))
    setSemester(parseInt(semester))
  }

  return (
    <div className="section">
      {Prompt}

      <Modal
        isOpen={largeModal}
        className="modal-lg"
        modalClassName="bd-example-modal-lg"
        toggle={() => setLargeModal(false)}
      >
        <div style={{ padding: 20 }}>
          <p
            style={{
              fontSize: 24,
              textAlign: 'center',
              fontWeight: 600,
              color: '#000',
            }}
          >
            Deseja enviar para sua lista de aprovados?
          </p>
          <p
            style={{
              fontSize: 14,
              letterSpacing: 2,
              marginBottom: 20,
              textAlign: 'center',
              fontWeight: 400,
              color: '#000',
            }}
          >
            Clicando em sim, suas pesquisas individuais serão armazenadas
            <br /> em sua lista de aprovados SiSU.
          </p>
          {isModalLoading ? (
            <Loading />
          ) : (
            <Fragment>
              <p>
                <Button
                  className="btn-round"
                  color="dark"
                  onClick={handleExit}
                  style={{
                    paddingTop: 10,
                    paddingBottom: 5,
                    fontSize: 14,
                    letterSpacing: 4,
                    textTransform: 'none',
                    color: '#FFF',
                    border: 0,
                    width: '100%',
                    height: 45,
                    borderRadius: 20,
                  }}
                >
                  Sim
                </Button>
              </p>

              <p style={{ marginTop: 10 }}>
                <Button
                  className="btn-round mr-1"
                  color="neutral"
                  onClick={goBack}
                  style={{
                    paddingTop: 10,
                    paddingBottom: 5,
                    fontSize: 14,
                    letterSpacing: 4,
                    textTransform: 'none',
                    color: '#000',
                    borderColor: '#000',
                    width: '100%',
                    height: 45,
                    borderRadius: 20,
                  }}
                >
                  Não
                </Button>
              </p>
            </Fragment>
          )}
        </div>
      </Modal>

      <Container className="text-center">
        <Row>
          <Col className="d-flex justify-content-between align-items-center">
            <select
              id="select-year"
              className="form-select"
              aria-label="Selecione o ano"
              onChange={handleYearChange}
              defaultValue={`${year}.${semester}`}
              style={{
                color: '#000',
                borderColor: '#aaa',
              }}
            >
              {years.map(item => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>

            <Button
              onClick={() => (studentsList?.length ? setLargeModal(true) : goBack())}
              className="btn-round"
              color="#000"
            >
              <span>X</span>
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <img src={logoMural} style={{ width: 260 }} alt="" />
          </Col>
        </Row>
      </Container>

      <Container className="text-center">
        <h2
          className="title"
          style={{
            fontWeight: 600,
            letterSpacing: 2,
            marginTop: 30,
            fontSize: 32,
          }}
        >
          Pesquisa individual
        </h2>
        <br />
        <p
          className="description"
          style={{
            letterSpacing: 2,
            color: '#000',
            marginTop: -20,
            lineHeight: 1.6,
          }}
        >
          Para consultar uma aprovação específica, aqui é o lugar! Coloque o nome completo do estudante e veja todas as
          informações como instituição de ensino e curso que ele foi aprovado, bem como a sua classificação.
        </p>

        <Form id="cadastro-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="title-brand" style={{ marginTop: 40, letterSpacing: 2, fontSize: 16 }}>
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText
                  style={{
                    borderRadius: 20,
                    marginRight: 10,
                    height: 45,
                    paddingLeft: 22,
                    paddingRight: 20,
                  }}
                >
                  <i className="nc-icon nc-single-02" />
                </InputGroupText>
              </InputGroupAddon>

              <Input
                innerRef={register}
                id="NomeCompleto"
                name="NomeCompleto"
                placeholder="Digite aqui o nome completo do estudante."
                type="text"
                style={{
                  borderRadius: 20,
                  height: 45,
                  paddingLeft: 20,
                  letterSpacing: 3,
                }}
              />
            </InputGroup>
            <ErrorText errors={errors} name="NomeCompleto" />
          </div>

          <div className="title-brand" style={{ marginTop: 20, letterSpacing: 4, fontSize: 16 }}>
            {isLoading ? (
              <Loading />
            ) : (
              <Button
                type="submit"
                className="btn-round"
                color="danger"
                style={{
                  paddingTop: 10,
                  paddingBottom: 5,
                  fontSize: 14,
                  letterSpacing: 4,
                  textTransform: 'none',
                  color: '#FFF',
                  border: 0,
                  width: '100%',
                  height: 45,
                  borderRadius: 20,
                }}
              >
                <i className="nc-icon nc-zoom-split" style={{ height: 18 }} /> Consultar
              </Button>
            )}
          </div>
        </Form>

        <Container>
          {isModalLoading ? (
            <Loading />
          ) : studentsList?.length > 0 ? (
            <Row md={{ size: 12 }} style={{ marginTop: 40, marginBottom: 20 }}>
              <Col md={{ size: 8 }} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  onClick={sendStudentList}
                  type="button"
                  style={{
                    color: '#FFF',
                    letterSpacing: 3,
                    borderRadius: 15,
                    backgroundColor: '#16057C',
                    borderWidth: 0,
                    textTransform: 'none',
                    fontSize: 14,
                    lineHeight: 1.5,
                    padding: '5px 15px',
                  }}
                >
                  Enviar para lista de aprovados
                </Button>
              </Col>
              <Col md={{ size: 4 }} style={{ display: 'flex', justifyContent: 'center' }}>
                <p
                  className="description"
                  style={{ letterSpacing: 1, color: '#000', lineHeight: 1.6, fontWeight: 600 }}
                >
                  Total de estudante{studentsList?.length > 1 ? 's' : ''}{' '}
                  <span style={{ fontSize: 22 }}>{studentsList?.length || 0}</span>
                </p>
              </Col>
            </Row>
          ) : null}
          <div style={{ marginTop: 30, marginBottom: 30 }}>
            <StudentsTable
              studentsList={studentsList}
              setStudentsList={setStudentsList}
              isDeleteButtonVisible
              deleteOnAPI={false}
              onDeleteClick={() => setDirty(false)}
            />
          </div>
        </Container>
      </Container>

      <WhiteFooter />
    </div>
  )
}

export default PesquisaIndividualPage
